import { WALLET_TYPES } from './WalletProvider.js';

/**
 * WalletManager - Central manager for wallet providers
 * 
 * Handles wallet provider registration, selection, and switching.
 * Maintains the active wallet provider and provides a consistent
 * interface for wallet operations across the application.
 */
class WalletManager {
  constructor() {
    this.providers = new Map();
    this.activeProvider = null;
    this.eventListeners = {
      accountsChanged: [],
      chainChanged: [],
      connect: [],
      disconnect: [],
      providerChanged: []
    };
    
    // Restore saved wallet type from localStorage
    this.savedWalletType = this.getSavedWalletType();
  }

  /**
   * Register a wallet provider
   * @param {WalletProvider} provider Wallet provider instance
   */
  registerProvider(provider) {
    if (!provider || typeof provider.getType !== 'function') {
      throw new Error('Invalid wallet provider - must implement WalletProvider interface');
    }
    
    const type = provider.getType();
    this.providers.set(type, provider);
    console.log(`🔌 Registered wallet provider: ${type}`);
    
    // Set up provider event forwarding
    this.setupProviderEventForwarding(provider);
  }

  /**
   * Get all registered wallet providers
   * @returns {Array} Array of provider objects with type and availability
   */
  getAvailableProviders() {
    const available = [];
    for (const [type, provider] of this.providers) {
      available.push({
        type,
        provider,
        displayName: provider.getDisplayName(),
        isAvailable: provider.isAvailable(),
        isConnected: provider.isConnected()
      });
    }
    return available;
  }

  /**
   * Set the active wallet provider
   * @param {string} walletType Wallet type identifier
   * @returns {Promise<boolean>} True if successfully set as active
   */
  async setActiveProvider(walletType) {
    const provider = this.providers.get(walletType);
    if (!provider) {
      throw new Error(`Wallet provider not found: ${walletType}`);
    }

    if (!provider.isAvailable()) {
      throw new Error(`Wallet provider not available: ${walletType}`);
    }

    console.log(`🔄 Switching to wallet provider: ${walletType}`);
    
    // Disconnect current provider if any
    if (this.activeProvider && this.activeProvider !== provider) {
      try {
        await this.activeProvider.disconnect();
      } catch (error) {
        console.warn('Error disconnecting previous wallet:', error);
      }
    }

    // Set new active provider
    const previousProvider = this.activeProvider;
    this.activeProvider = provider;
    
    // Save wallet type preference
    this.saveWalletType(walletType);
    
    // Emit provider change event
    this.emit('providerChanged', { 
      newProvider: provider, 
      previousProvider,
      walletType 
    });
    
    return true;
  }

  /**
   * Get the currently active provider
   * @returns {WalletProvider|null} Active provider or null
   */
  getActiveProvider() {
    return this.activeProvider;
  }

  /**
   * Connect to the active wallet provider
   * @returns {Promise<void>}
   */
  async connect() {
    if (!this.activeProvider) {
      throw new Error('No active wallet provider set');
    }
    
    await this.activeProvider.connect();
    console.log(`✅ Connected to ${this.activeProvider.getType()} wallet`);
  }

  /**
   * Disconnect from the active wallet provider
   * @returns {Promise<void>}
   */
  async disconnect() {
    if (this.activeProvider) {
      await this.activeProvider.disconnect();
      console.log(`🔌 Disconnected from ${this.activeProvider.getType()} wallet`);
    }
  }

  /**
   * Auto-connect to the last used wallet provider
   * @returns {Promise<boolean>} True if auto-connect succeeded
   */
  async autoConnect() {
    if (!this.savedWalletType) {
      console.log('🔍 No saved wallet type for auto-connect');
      return false;
    }

    const provider = this.providers.get(this.savedWalletType);
    if (!provider || !provider.isAvailable()) {
      console.log(`🔍 Saved wallet type ${this.savedWalletType} not available for auto-connect`);
      return false;
    }

    try {
      console.log(`🔄 Auto-connecting to ${this.savedWalletType}...`);
      
      // Handle Custom RPC Wallet auto-connect specially since it requires saved key
      if (this.savedWalletType === WALLET_TYPES.CUSTOM_RPC) {
        if (provider.hasSavedKey()) {
          try {
            // Try to auto-connect using saved private key first
            const autoConnectSuccess = await provider.autoConnectFromSaved();
            if (autoConnectSuccess) {
              // Only set as active provider after successful connection
              await this.setActiveProvider(this.savedWalletType);
              console.log('✅ Auto-connected using saved private key');
              return true;
            } else {
              console.log('🔍 Failed to auto-connect with saved private key');
              return false;
            }
          } catch (error) {
            console.warn('❌ Auto-connect failed with saved private key:', error.message);
            return false;
          }
        } else {
          console.log('🔍 Private key wallet saved but no key in storage, skipping auto-connect');
          return false;
        }
      }
      
      // For other wallet types (MetaMask), set as active first then check connection
      await this.setActiveProvider(this.savedWalletType);
      
      // For MetaMask, check if already connected and ensure proper connection
      if (this.savedWalletType === WALLET_TYPES.METAMASK) {
        const accounts = await provider.getAccounts();
        if (accounts.length === 0) {
          console.log('🔍 MetaMask not connected, skipping auto-connect');
          this.activeProvider = null; // Clear invalid provider
          return false;
        }

        // CRITICAL: Ensure MetaMask is properly connected with signer
        // Auto-connection only sets provider, but we need signer for isConnected() to work
        if (!provider.isConnected()) {
          console.log('🔧 MetaMask has accounts but missing signer, calling connect()...');
          await provider.connect();
        }
      }

      console.log(`✅ Auto-connected to ${this.savedWalletType}`);
      return true;
    } catch (error) {
      console.warn(`❌ Auto-connect failed for ${this.savedWalletType}:`, error.message);
      return false;
    }
  }

  // Delegate methods to active provider
  getProvider() {
    return this.activeProvider?.getProvider() || null;
  }

  getSigner() {
    return this.activeProvider?.getSigner() || null;
  }

  async getAccounts() {
    if (!this.activeProvider) return [];
    return await this.activeProvider.getAccounts();
  }

  async getNetwork() {
    if (!this.activeProvider) return null;
    return await this.activeProvider.getNetwork();
  }

  isConnected() {
    return this.activeProvider?.isConnected() || false;
  }

  getErrorMessage() {
    return this.activeProvider?.getErrorMessage() || '';
  }

  createContract(address, abi, needsSigner = false) {
    if (!this.activeProvider) {
      throw new Error('No active wallet provider for contract creation');
    }
    return this.activeProvider.createContract(address, abi, needsSigner);
  }

  getEventConfig() {
    return this.activeProvider?.getEventConfig() || {
      fallbackInterval: 5000,
      connectionTimeout: 30000,
      supportsRealTimeEvents: true
    };
  }

  // Event handling
  on(event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].push(callback);
    }
  }

  off(event, callback) {
    if (this.eventListeners[event]) {
      const index = this.eventListeners[event].indexOf(callback);
      if (index > -1) {
        this.eventListeners[event].splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in wallet manager event listener for ${event}:`, error);
        }
      });
    }
  }

  // Private methods
  setupProviderEventForwarding(provider) {
    // Forward provider events to wallet manager listeners
    provider.onAccountsChanged?.((accounts) => {
      this.emit('accountsChanged', accounts);
    });

    provider.onChainChanged?.((chainId) => {
      this.emit('chainChanged', chainId);
    });

    provider.onConnect?.((connectInfo) => {
      this.emit('connect', connectInfo);
    });

    provider.onDisconnect?.((error) => {
      this.emit('disconnect', error);
    });
  }

  saveWalletType(walletType) {
    try {
      localStorage.setItem('gamemaker_wallet_type', walletType);
      this.savedWalletType = walletType;
    } catch (error) {
      console.warn('Failed to save wallet type preference:', error);
    }
  }

  getSavedWalletType() {
    try {
      return localStorage.getItem('gamemaker_wallet_type');
    } catch (error) {
      console.warn('Failed to load wallet type preference:', error);
      return null;
    }
  }

  clearSavedWalletType() {
    try {
      localStorage.removeItem('gamemaker_wallet_type');
      this.savedWalletType = null;
    } catch (error) {
      console.warn('Failed to clear wallet type preference:', error);
    }
  }
}

// Create singleton instance
const walletManager = new WalletManager();

export default walletManager;
export { WalletManager };